import { MaintenanceProgrammeService } from "@common/client"
import { makeAxiosMutation, makeNoArgsQuery, makeQuery } from "./utils"

export const programmesBaseKey = ["maintenance-programmes"] as const

export const programmesListKey = (params?: { limit?: number }) =>
  [...programmesBaseKey, "list", params] as const

export const programmesQuery = makeQuery({
  serviceCall: MaintenanceProgrammeService.listProgrammes,
  queryKey: (params) => programmesListKey(params),
})

export const programmeKey = (
  params: Parameters<typeof MaintenanceProgrammeService.getProgramme>[0],
) => [...programmesBaseKey, "detail", params] as const

export const programmeRevisionsKey = (
  params: Parameters<
    typeof MaintenanceProgrammeService.listProgrammeRevisions
  >[0],
) => [...programmesBaseKey, "revisions", params] as const

export const programmeTasksKey = (
  params: Parameters<typeof MaintenanceProgrammeService.listProgrammeTasks>[0],
) => [...programmesBaseKey, "tasks", params] as const

export const programmeChecksKey = (
  params: Parameters<typeof MaintenanceProgrammeService.listProgrammeChecks>[0],
) => [...programmesBaseKey, "checks", params] as const

export const programmeAircraftKey = (
  params: Parameters<
    typeof MaintenanceProgrammeService.listProgrammeAircraft
  >[0],
) => [...programmesBaseKey, "aircraft", params] as const

export const programmeActivityKey = (
  params: Parameters<
    typeof MaintenanceProgrammeService.listProgrammeActivity
  >[0],
) => [...programmesBaseKey, "activity", params] as const

export const programmeActivityQuery = makeQuery({
  serviceCall: MaintenanceProgrammeService.listProgrammeActivity,
  queryKey: programmeActivityKey,
})

export const programmeQuery = makeQuery({
  serviceCall: MaintenanceProgrammeService.getProgramme,
  queryKey: programmeKey,
})

export const programmeRevisionsQuery = makeQuery({
  serviceCall: MaintenanceProgrammeService.listProgrammeRevisions,
  queryKey: programmeRevisionsKey,
})

export const programmeTasksQuery = makeQuery({
  serviceCall: MaintenanceProgrammeService.listProgrammeTasks,
  queryKey: programmeTasksKey,
})

export const programmeChecksQuery = makeQuery({
  serviceCall: MaintenanceProgrammeService.listProgrammeChecks,
  queryKey: programmeChecksKey,
})

export const programmeAircraftQuery = makeQuery({
  serviceCall: MaintenanceProgrammeService.listProgrammeAircraft,
  queryKey: programmeAircraftKey,
})

export const aircraftEnrolmentsKey = (
  params: Parameters<
    typeof MaintenanceProgrammeService.listAircraftEnrolments
  >[0],
) => [...programmesBaseKey, "enrolments", params] as const

export const aircraftEnrolmentsQuery = makeQuery({
  serviceCall: MaintenanceProgrammeService.listAircraftEnrolments,
  queryKey: aircraftEnrolmentsKey,
})

export const programmeOverviewQuery = makeNoArgsQuery({
  serviceCall: MaintenanceProgrammeService.getProgrammeOverview,
  queryKey: [...programmesBaseKey, "overview"],
})

export const trackedComponentsKey = (
  params: Parameters<
    typeof MaintenanceProgrammeService.listTrackedComponents
  >[0],
) => [...programmesBaseKey, "tracked-components", params] as const

export const trackedComponentsQuery = makeQuery({
  serviceCall: MaintenanceProgrammeService.listTrackedComponents,
  queryKey: trackedComponentsKey,
  staleTime: 60_000,
})

export const taskTrackedComponentsKey = (
  params: Parameters<
    typeof MaintenanceProgrammeService.listTaskTrackedComponents
  >[0],
) => [...programmesBaseKey, "task-tracked-components", params] as const

export const taskTrackedComponentsQuery = makeQuery({
  serviceCall: MaintenanceProgrammeService.listTaskTrackedComponents,
  queryKey: taskTrackedComponentsKey,
  staleTime: 60_000,
})

export const programmeNextNumberQuery = makeNoArgsQuery({
  serviceCall: MaintenanceProgrammeService.nextProgrammeNumber,
  queryKey: [...programmesBaseKey, "next-number"],
})

export const useArchiveProgrammesMutation = makeAxiosMutation({
  serviceCall: MaintenanceProgrammeService.archiveProgrammes,
  invalidates: () => [programmesBaseKey],
})

export const useRestoreProgrammesMutation = makeAxiosMutation({
  serviceCall: MaintenanceProgrammeService.restoreProgrammes,
  invalidates: () => [programmesBaseKey],
})

export const useDeleteProgrammesMutation = makeAxiosMutation({
  serviceCall: MaintenanceProgrammeService.deleteProgrammes,
  invalidates: () => [programmesBaseKey],
})

export const useCreateProgrammeMutation = makeAxiosMutation({
  serviceCall: MaintenanceProgrammeService.createProgramme,
  invalidates: () => [programmesBaseKey],
})

export const useUpdateProgrammeMutation = makeAxiosMutation({
  serviceCall: MaintenanceProgrammeService.updateProgramme,
  invalidates: () => [programmesBaseKey],
})

export const useUpdateProgrammeRevisionMutation = makeAxiosMutation({
  serviceCall: MaintenanceProgrammeService.applyProgrammeRevisionUpdate,
  invalidates: () => [programmesBaseKey],
})

export const useAddProgrammeTaskMutation = makeAxiosMutation({
  serviceCall: MaintenanceProgrammeService.addProgrammeTask,
  invalidates: () => [programmesBaseKey],
})

export const useReplaceProgrammeTaskMutation = makeAxiosMutation({
  serviceCall: MaintenanceProgrammeService.replaceProgrammeTask,
  invalidates: () => [programmesBaseKey],
})

export const useRemoveProgrammeTaskMutation = makeAxiosMutation({
  serviceCall: MaintenanceProgrammeService.removeProgrammeTask,
  invalidates: () => [programmesBaseKey],
})

export const useAddProgrammeCheckMutation = makeAxiosMutation({
  serviceCall: MaintenanceProgrammeService.addProgrammeCheck,
  invalidates: () => [programmesBaseKey],
})

export const useReplaceProgrammeCheckMutation = makeAxiosMutation({
  serviceCall: MaintenanceProgrammeService.replaceProgrammeCheck,
  invalidates: () => [programmesBaseKey],
})

export const useRemoveProgrammeCheckMutation = makeAxiosMutation({
  serviceCall: MaintenanceProgrammeService.removeProgrammeCheck,
  invalidates: () => [programmesBaseKey],
})

export const useCreateProgrammeRevisionMutation = makeAxiosMutation({
  serviceCall: MaintenanceProgrammeService.createProgrammeRevision,
  invalidates: () => [programmesBaseKey],
})

export const useReleaseProgrammeRevisionMutation = makeAxiosMutation({
  serviceCall: MaintenanceProgrammeService.releaseProgrammeRevision,
  invalidates: () => [programmesBaseKey],
})

export const useEnrolProgrammeAircraftMutation = makeAxiosMutation({
  serviceCall: MaintenanceProgrammeService.enrolProgrammeAircraft,
  invalidates: () => [programmesBaseKey],
})

export const useUnenrolProgrammeAircraftMutation = makeAxiosMutation({
  serviceCall: MaintenanceProgrammeService.unenrolProgrammeAircraft,
  invalidates: () => [programmesBaseKey],
})

export const useSendProgrammeToSalesforceMutation = makeAxiosMutation({
  serviceCall: MaintenanceProgrammeService.sendProgrammeToSalesforce,
  invalidates: () => [programmesBaseKey],
})
